import { translateErrorCode, useTranslation } from '@jq4race/i18n';
import {
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
} from '@jq4race/ui';
import { AlertTriangle } from 'lucide-react';
import { useState } from 'react';

import type { OutboxItem } from '../../domain/outbox-item.js';
import { editableFieldFor, rejectedActionsFor } from '../../domain/rejected-actions.js';

export interface RejectedItemCardProps {
  item: OutboxItem;
  /** Confirms the operator's explicit "submit anyway" decision (design's `force` action). */
  onForce: () => void;
  /** Confirms a corrected-field retry — `patch` is merged into the item's payload. */
  onEditRetry: (patch: Record<string, unknown>) => void;
  /** Confirmed via the in-card dialog — never called on a bare click. */
  onDiscard: () => void;
}

/**
 * RejectedItemCard (pwa-offline-first Slice 7, design §5 — conflict UX) —
 * per-item rejected state with a human-readable reason and the actions
 * `rejectedActionsFor` says apply to THIS item's `lastError.code`:
 * `force` (duplicate_active_ticket), `edit` (a field-level 422 or
 * qr_tag_conflict — a minimal, single-field plate editor; see
 * `resolveRejected`'s own doc comment for why a full multi-field editor is
 * out of this slice's scope), and `discard` (always available, gated
 * behind an explicit confirmation — this is a PERMANENT, unrecoverable
 * decision).
 */
export function RejectedItemCard({ item, onForce, onEditRetry, onDiscard }: RejectedItemCardProps) {
  const { t } = useTranslation('pending');
  const [editing, setEditing] = useState(false);
  const [confirmingDiscard, setConfirmingDiscard] = useState(false);

  const code = item.lastError?.code ?? 'unknown';
  const actions = rejectedActionsFor(code);
  // MEDIUM fix (adversarial review) — WHICH field this card's single-field
  // editor targets depends on the rejection code (qrTag for
  // qr_tag_conflict, plate otherwise) — editing the wrong field re-rejected
  // the item forever. `editableField === null` (e.g. ticket_type_disabled,
  // niche_field_mismatch) means Edit is never shown at all, even if a
  // caller somehow still passed 'edit' in `actions`.
  const editableField = editableFieldFor(code);
  const [editValue, setEditValue] = useState(
    editableField && typeof item.payload[editableField] === 'string'
      ? (item.payload[editableField] as string)
      : '',
  );
  const status = item.lastError?.status;
  // A bare 403 (no recognizable code) used to render the catalogue's "you have
  // no permission" copy, which tells the valet nothing about what happened.
  const isBare403 = status === 403 && (code === 'unknown' || code === 'forbidden');
  const reason = isBare403
    ? t('pendingItems.rejectReason.forbidden')
    : t(`pendingItems.rejectReason.${code}`, {
        // Codes without a dedicated card copy use the shared catalogue — never the
        // raw server message, which is English developer text.
        defaultValue: translateErrorCode(code, t, {
          status,
          details: { field: item.lastError?.field },
        }),
      });

  return (
    <>
      <Card className="border-destructive/40 bg-card">
        <CardContent className="space-y-3 px-4 py-3.5">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="jq-data tabular-nums text-jq-gold-bright">{item.provisionalCode}</p>
              <p className="jq-mono jq-body-sm truncate text-foreground">
                {typeof item.payload.plate === 'string' ? item.payload.plate : ''}
              </p>
            </div>
            <span
              role="alert"
              className="shrink-0 text-[12px] font-medium uppercase tracking-wide text-destructive"
            >
              {t('pendingItems.stateRejected')}
            </span>
          </div>

          <p className="jq-body-sm text-foreground">{reason}</p>
          <p className="jq-body-sm text-muted-foreground">{t('pendingItems.rejectedFooter')}</p>

          {editing && editableField ? (
            <div className="flex items-center gap-2">
              <Input
                aria-label={
                  editableField === 'qrTag'
                    ? t('pendingItems.editQrTagLabel')
                    : editableField === 'phone'
                      ? t('pendingItems.editPhoneLabel')
                      : t('pendingItems.editPlateLabel')
                }
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
              />
              <Button size="sm" onClick={() => onEditRetry({ [editableField]: editValue })}>
                {t('pendingItems.actionRetry')}
              </Button>
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {actions.includes('force') && (
                <Button size="sm" onClick={onForce}>
                  {t('pendingItems.actionForce')}
                </Button>
              )}
              {actions.includes('edit') && editableField && (
                <Button size="sm" variant="secondary" onClick={() => setEditing(true)}>
                  {t('pendingItems.actionEdit')}
                </Button>
              )}
              <Button size="sm" variant="destructive" onClick={() => setConfirmingDiscard(true)}>
                {t('pendingItems.actionDiscard')}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {confirmingDiscard && (
        <Dialog open onOpenChange={(open) => !open && setConfirmingDiscard(false)}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-destructive/15">
                  <AlertTriangle className="size-5 text-destructive" strokeWidth={1.5} />
                </span>
                <DialogTitle className="text-[20px]">
                  {t('pendingItems.discardConfirmTitle')}
                </DialogTitle>
              </div>
              <DialogDescription className="mt-1">
                {t('pendingItems.discardConfirmDescription')}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="ghost"
                size="lg"
                className="w-full sm:w-auto"
                onClick={() => setConfirmingDiscard(false)}
              >
                {t('common:actions.cancel')}
              </Button>
              <Button
                variant="destructive"
                size="lg"
                className="w-full sm:w-auto"
                onClick={onDiscard}
              >
                {t('pendingItems.discardConfirmButton')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
